// Phase 31 (plan 02): waveform + play/pause player for voice/audio messages.
// Extracted verbatim from miniapp/src/components/tickets/MessageList.tsx — only
// the file location changed.

import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useVoiceRoom } from './VoiceSpeedContext';

const DEFAULT_WAVEFORM_BARS = [0.28, 0.42, 0.64, 0.88, 0.56, 0.34, 0.7, 0.92, 0.6, 0.4, 0.68, 0.5];

function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '0:00';
  const seconds = Math.floor(totalSeconds);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
}

function getAudioDuration(audio: HTMLAudioElement): number {
  return Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
}

function buildWaveformBars(samples: Float32Array, count: number): number[] {
  if (samples.length === 0 || count <= 0) return DEFAULT_WAVEFORM_BARS;

  const bucketSize = Math.max(1, Math.floor(samples.length / count));
  const amplitudes = Array.from({ length: count }, (_, idx) => {
    const start = idx * bucketSize;
    const end = Math.min(samples.length, start + bucketSize);
    let peak = 0;

    for (let cursor = start; cursor < end; cursor += 1) {
      peak = Math.max(peak, Math.abs(samples[cursor] ?? 0));
    }

    return peak;
  });

  const maxAmplitude = Math.max(...amplitudes, 0);
  if (maxAmplitude <= 0) return DEFAULT_WAVEFORM_BARS;

  return amplitudes.map((value) => Math.max(0.18, Math.min(1, value / maxAmplitude)));
}

// Tap-through playback speeds (Messenger-style). Always starts at 1× and
// wraps back to 1× after the top step.
const PLAYBACK_SPEEDS = [1, 1.5, 2, 3] as const;

function formatRate(rate: number): string {
  // Drop a trailing ".0" so 2× shows "2x" not "2.0x", but keep "1.5x".
  return `${Number.isInteger(rate) ? rate : rate.toFixed(1)}x`;
}

interface VoicePlayerProps {
  audioUrl: string;
  presentation?: 'bubble' | 'bare';
  isFromUser: boolean;
  /** Authoritative whole-second duration from the server. Preferred over the
   *  audio element / decoded buffer because OGG/Opus duration is unreliable
   *  to read client-side on iOS WebView. */
  durationSeconds?: number;
  /** Message sequence (ascending with time) used to chain auto-advance: when
   *  this voice ends, the room plays the next-higher seq. Omitted → no
   *  auto-advance (and no room participation). */
  seq?: number;
}

export function VoicePlayer({ audioUrl, presentation = 'bubble', isFromUser, durationSeconds, seq }: VoicePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const frameRef = useRef<number | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  // Fallback duration recovered from decoding the file (used when the server
  // didn't supply one — e.g. messages predating migration 032).
  const [decodedDuration, setDecodedDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(DEFAULT_WAVEFORM_BARS);

  // Playback speed is shared across every voice in the chat room (sticky until
  // you leave the room). Outside a room provider — e.g. the Bayon embed — fall
  // back to this player's own local speed so it still works standalone.
  const room = useVoiceRoom();
  const [localRate, setLocalRate] = useState(1);
  const playbackRate = room ? room.playbackRate : localRate;
  const setPlaybackRate = room ? room.setPlaybackRate : setLocalRate;

  // Authoritative duration, in preference order: server value → decoded
  // buffer → audio element. Keeps the time readable even when the browser
  // can't decode the OGG/Opus container.
  const effectiveDuration =
    durationSeconds && durationSeconds > 0
      ? durationSeconds
      : decodedDuration > 0
        ? decodedDuration
        : duration;

  const isBare = presentation === 'bare';
  const isOutgoing = isBare ? !isFromUser : isFromUser;
  const bubbleBase = isOutgoing ? '#E7FECC' : '#FFFFFF';
  const accentColor = isOutgoing ? '#439636' : '#7E919A';
  const background = isBare
    ? bubbleBase
    : isFromUser
      ? 'rgba(255,255,255,0.22)'
      : 'rgba(0,0,0,0.08)';
  const border = isBare ? '1px solid rgba(0,0,0,0.06)' : 'none';
  const playButtonBg = isOutgoing ? 'rgba(67,150,54,0.14)' : 'rgba(126,145,154,0.12)';
  const iconColor = accentColor;
  const barColor = accentColor;
  const barIdleColor = isOutgoing ? 'rgba(67,150,54,0.28)' : 'rgba(126,145,154,0.3)';
  const timeColor = accentColor;
  const timeLeft = effectiveDuration > 0 ? Math.max(0, effectiveDuration - currentTime) : 0;
  const playedRatio = effectiveDuration > 0 ? Math.min(1, currentTime / effectiveDuration) : 0;

  // Ask the room to play the next voice when this one ends. Held in a ref so
  // the (mount-once) ended handler always sees the current room/seq without
  // re-subscribing. No-op outside a room provider or when seq is unknown.
  const advanceRef = useRef<() => void>(() => {});
  advanceRef.current = () => {
    if (room && seq != null) room.playNext(seq);
  };

  // Register this voice's play() with the room so a preceding voice can chain
  // into it. Starts from the beginning each time it's auto-played.
  useEffect(() => {
    if (!room || seq == null) return undefined;
    return room.registerVoice(seq, () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = 0;
      audio.play().catch(() => undefined);
    });
  }, [room, seq]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const syncTiming = () => {
      setDuration(getAudioDuration(audio));
      setCurrentTime(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      advanceRef.current();
      // Note: the selected speed is intentionally NOT reset here — it persists
      // across voices for the whole room (VoiceSpeedProvider), resetting only
      // when the room unmounts / the ticket changes.
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    syncTiming();

    audio.addEventListener('loadedmetadata', syncTiming);
    audio.addEventListener('loadeddata', syncTiming);
    audio.addEventListener('durationchange', syncTiming);
    audio.addEventListener('canplay', syncTiming);
    audio.addEventListener('timeupdate', syncTiming);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadedmetadata', syncTiming);
      audio.removeEventListener('loadeddata', syncTiming);
      audio.removeEventListener('durationchange', syncTiming);
      audio.removeEventListener('canplay', syncTiming);
      audio.removeEventListener('timeupdate', syncTiming);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadWaveform = async () => {
      const AudioContextCtor = window.AudioContext || (window as Window & {
        webkitAudioContext?: typeof AudioContext;
      }).webkitAudioContext;

      if (!AudioContextCtor) return;

      let decodeContext: AudioContext | null = null;

      try {
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error('Unable to load audio');

        const fileBuffer = await response.arrayBuffer();
        decodeContext = new AudioContextCtor();
        const decodedBuffer = await decodeContext.decodeAudioData(fileBuffer.slice(0));
        const samples = decodedBuffer.getChannelData(0);

        if (!isCancelled) {
          setWaveform(buildWaveformBars(samples, DEFAULT_WAVEFORM_BARS.length));
          // decodeAudioData yields an exact duration even when the OGG/Opus
          // HTMLAudioElement reports 0/NaN — use it as the fallback when the
          // server didn't supply one.
          if (Number.isFinite(decodedBuffer.duration) && decodedBuffer.duration > 0) {
            setDecodedDuration(decodedBuffer.duration);
          }
        }
      } catch {
        if (!isCancelled) {
          setWaveform(DEFAULT_WAVEFORM_BARS);
        }
      } finally {
        if (decodeContext) {
          decodeContext.close().catch(() => undefined);
        }
      }
    };

    loadWaveform();

    return () => {
      isCancelled = true;
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!isPlaying) {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      return undefined;
    }

    const tick = () => {
      const audio = audioRef.current;
      if (audio) {
        setCurrentTime(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [isPlaying]);

  // Keep the audio element's rate in sync with the selected speed.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = playbackRate;
  }, [playbackRate]);

  // Tap the speed pill to step up; wraps 1× → 1.5× → 2× → 3× → 1×.
  const handleCycleSpeed = () => {
    const idx = PLAYBACK_SPEEDS.indexOf(playbackRate as (typeof PLAYBACK_SPEEDS)[number]);
    setPlaybackRate(PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length]);
  };

  const handleTogglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
      }
    } catch {
      setIsPlaying(false);
    }
  };

  return (
    <div
      className={isBare ? 'rounded-[24px] px-3 py-2 shadow-sm' : 'rounded-[18px] px-2.5 py-2 mb-1'}
      style={{
        background,
        border,
        minWidth: isBare ? '240px' : '200px',
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="auto" crossOrigin="anonymous" className="hidden" />

      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={handleTogglePlayback}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition"
          style={{ background: playButtonBg }}
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        >
          {isPlaying ? (
            <Pause className="h-4.5 w-4.5" style={{ color: iconColor }} strokeWidth={2.5} aria-hidden />
          ) : (
            <Play className="h-4.5 w-4.5 ml-0.5" style={{ color: iconColor }} fill={iconColor} strokeWidth={2.5} aria-hidden />
          )}
        </button>

        <div className="flex items-end gap-1 h-8 flex-1 min-w-[96px]">
          {waveform.map((value, index) => (
            (() => {
              const barStart = index / waveform.length;
              const barEnd = (index + 1) / waveform.length;
              const isPassed = playedRatio >= barEnd;
              const isCurrent = playedRatio >= barStart && playedRatio < barEnd;

              return (
                <span
                  key={`${audioUrl}-${index}`}
                  className="w-[4px] rounded-full transition-all duration-150"
                  style={{
                    height: `${10 + value * 22 + (isCurrent ? 2 : 0)}px`,
                    background: isPassed || isCurrent ? barColor : barIdleColor,
                    opacity: isPassed || isCurrent ? 1 : 0.82,
                  }}
                />
              );
            })()
          ))}
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className="text-[13px] font-semibold tabular-nums"
            style={{ color: timeColor }}
          >
            {formatDuration(isPlaying ? timeLeft : effectiveDuration)}
          </span>

          {/* Speed control — only while playing, like Messenger. Tap to step
              up; resets to 1× on each fresh play. */}
          {isPlaying && (
            <button
              type="button"
              onClick={handleCycleSpeed}
              className="rounded-full px-2 py-0.5 text-[11px] font-bold leading-none tabular-nums transition active:scale-95"
              style={{ background: playButtonBg, color: accentColor }}
              aria-label={`Playback speed ${formatRate(playbackRate)}, tap to change`}
            >
              {formatRate(playbackRate)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
